import { useState } from "react";
import { saveStrategy } from "../services/api";
import type { Strategy, StrategyMetricsInput } from "../services/api";

interface SaveStrategyModalProps {
  isOpen: boolean;
  onClose: () => void;
  strategyCode: string;
  metrics: StrategyMetricsInput;
  suggestedName?: string;
  onSaveSuccess?: (strategy: Strategy) => void;
}

const AVAILABLE_TAGS = [
  "Momentum",
  "Value",
  "Growth",
  "Dividend",
  "Bond",
  "Leveraged",
  "Hedged",
  "VIX",
  "Tech",
  "Defensive",
];

export function SaveStrategyModal({
  isOpen,
  onClose,
  strategyCode,
  metrics,
  suggestedName,
  onSaveSuccess,
}: SaveStrategyModalProps) {
  const [name, setName] = useState(suggestedName || "");
  const [description, setDescription] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleTagToggle = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Please enter a strategy name");
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await saveStrategy({
        name: name.trim(),
        description: description.trim(),
        tags: selectedTags,
        code: strategyCode,
        metrics: metrics,
        is_community: true,
      });

      setSuccess(true);
      setTimeout(() => {
        onClose();
        if (response.strategy && onSaveSuccess) {
          onSaveSuccess(response.strategy);
        }
        setSuccess(false);
        setName("");
        setDescription("");
        setSelectedTags([]);
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save strategy");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-surface-900 border border-surface-700 rounded-2xl shadow-2xl w-full max-w-lg animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-surface-700">
          <h2 className="text-xl font-semibold text-surface-100">
            Save to Community
          </h2>
          <button
            onClick={onClose}
            className="text-surface-400 hover:text-surface-200 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto bg-accent-500/20 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-accent-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-surface-100">
                Strategy Saved!
              </h3>
              <p className="text-surface-400 mt-2">
                Your strategy is now available in Community Strategies
              </p>
            </div>
          ) : (
            <>
              {/* Strategy Name */}
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Strategy Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="My Awesome Strategy"
                  className="input-field w-full"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of your strategy..."
                  className="input-field w-full h-24 resize-none"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2">
                  {AVAILABLE_TAGS.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => handleTagToggle(tag)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-full transition-all duration-200 ${
                        selectedTags.includes(tag)
                          ? "bg-primary-500 text-white shadow-lg shadow-primary-500/25"
                          : "bg-surface-800 text-surface-400 hover:bg-surface-700 border border-surface-700/50"
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Metrics Preview */}
              <div className="bg-surface-800/50 rounded-xl p-4">
                <div className="text-xs text-surface-500 mb-2">
                  Backtest Metrics (will be saved)
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div
                      className={`text-lg font-bold ${metrics.cagr >= 0 ? "text-accent-400" : "text-red-400"}`}
                    >
                      {metrics.cagr >= 0 ? "+" : ""}
                      {metrics.cagr.toFixed(1)}%
                    </div>
                    <div className="text-xs text-surface-500">CAGR</div>
                  </div>
                  <div className="text-center border-x border-surface-700">
                    <div className="text-lg font-bold text-surface-200">
                      {metrics.sharpe.toFixed(2)}
                    </div>
                    <div className="text-xs text-surface-500">Sharpe</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-red-400">
                      {metrics.maxDrawdown.toFixed(1)}%
                    </div>
                    <div className="text-xs text-surface-500">Max DD</div>
                  </div>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-3 flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-red-400 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-red-400 text-sm">{error}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <div className="flex items-center justify-end gap-3 p-6 border-t border-surface-700">
            <button onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !name.trim()}
              className="btn-primary flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <span className="spinner" />
                  Saving...
                </>
              ) : (
                <>
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                    />
                  </svg>
                  Save Strategy
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
